import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { apiRequest, isApiError } from "@/shared/api";
import type { ApiError } from "@/shared/api";
import {
  normalizeAuthResponse,
  type NormalizedAuthResponse,
} from "./normalize-auth-response";

export type LoginPayload = {
  email: string;
  password: string;
};

export type LoginResponse = NormalizedAuthResponse & {
  accessToken: string;
};

const loginRequest = (payload: LoginPayload) => ({
  url: "/auth/login",
  method: "post",
  data: payload,
});

export const login = async (payload: LoginPayload): Promise<LoginResponse> => {
  const response = await apiRequest<unknown>(loginRequest(payload));
  const normalized = normalizeAuthResponse(response, payload.email);

  if (!normalized.accessToken) {
    throw new Error("Missing access token in login response");
  }

  return {
    ...normalized,
    accessToken: normalized.accessToken,
  };
};

export const useLogin = (
  options?: UseMutationOptions<LoginResponse, ApiError, LoginPayload>
) =>
  useMutation<LoginResponse, ApiError, LoginPayload>({
    mutationFn: login,
    ...options,
    onError: (error, variables, onMutateResult, context) => {
      if (!isApiError(error)) {
        console.error("[auth] Unexpected login error", error);
      }

      options?.onError?.(error, variables, onMutateResult, context);
    },
  });
